
import { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';

import { Routes } from '@share/constants';
import { IAccount, LOGOUT_FIELD_KEY, INavigation } from '@share/common-types';
import { getPreferenceFromAccount, LoginType } from '@share/utils';
import { logout, setLoginRedirect } from '@share/store/slices';
import Cookies from 'js-cookie';
import { COOKIE1_TOKEN_NAME, COOKIE_TOKEN_NAME } from '@share/constants';

import './style.scss';

interface IProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  accountName: string;
  userName: string;
  memberId: string;
  points?: number;
  logoUrl?: string;
  account?: IAccount;
  items?: INavigation;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function ModernSidebar(props: IProps) {
  const { collapsed, onToggleCollapse, accountName, userName, memberId, points, logoUrl, account, items, mobileOpen = false, onMobileClose } = props;
  
  const [selectedItem, setSelectedItem] = useState<string>('home-icon');

  const dispatch = useDispatch();
  
  const history = useHistory();

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleLinkClick = (item?: string) => {
    if (item) {
      setSelectedItem(item);
    }

    if (onMobileClose) {
      onMobileClose();
    }
  };

  return (
    <aside className={`modern-sidebar ${collapsed ? 'modern-sidebar--collapsed' : ''} ${mobileOpen ? 'modern-sidebar--mobile-open' : ''}`}>
      <div className="modern-sidebar__header">
        {!collapsed && !mobileOpen && (
          <Link to={`/${accountName}${Routes.Home}`} className="modern-sidebar__brand" onClick={() => handleLinkClick()}>
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="modern-sidebar__brand-logo" />
            ) : (
              <>
                <h1 className="modern-sidebar__brand-title">TravelCore</h1>
                <p className="modern-sidebar__brand-subtitle">Member Portal</p>
              </>
            )}
          </Link>
        )}
        {!mobileOpen && (
          <button
            onClick={onToggleCollapse}
            className="modern-sidebar__toggle"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
          <svg
            className="modern-sidebar__toggle-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {collapsed ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 5l7 7-7 7M5 5l7 7-7 7"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
              />
            )}
          </svg>
          </button>
        )}
      </div>

      <nav className="modern-sidebar__nav">
        <Link
          to={`/${accountName}${Routes.Home}`}
          className={`modern-sidebar__nav-item ${selectedItem === 'home-icon' ? 'modern-sidebar__nav-item--active' : ''}`}
          title="Home"
          onClick={() => handleLinkClick('home-icon')}
        >
          <svg
            className="modern-sidebar__nav-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
          {!collapsed && <span>Home</span>}
        </Link>

        <Link
          to={`/${accountName}${Routes.Search}`}
          className={`modern-sidebar__nav-item ${selectedItem === 'search-icon' ? 'modern-sidebar__nav-item--active' : ''}`}
          title="Search Hotels"
          onClick={() => handleLinkClick('search-icon')}
        >
          <svg
            className="modern-sidebar__nav-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {!collapsed && <span>Search Hotels</span>}
        </Link>

        <Link
          to={`/${accountName}${Routes.CondoSearch}`}
          className={`modern-sidebar__nav-item ${selectedItem === 'condo-icon' ? 'modern-sidebar__nav-item--active' : ''}`}
          title="Search Condos"
          onClick={() => handleLinkClick('condo-icon')}
        >
          <svg
            className="modern-sidebar__nav-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
          {!collapsed && <span>Search Condos</span>}
        </Link>

        <Link
          to={`/${accountName}${Routes.CarsSearch}`}
          className={`modern-sidebar__nav-item ${selectedItem === 'car-icon' ? 'modern-sidebar__nav-item--active' : ''}`}
          title="Search Car Rentals"
          onClick={() => handleLinkClick('car-icon')}
        >
          <svg
            className="modern-sidebar__nav-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h.01M14 12h.01"
            />
            <circle cx="7" cy="18" r="2" />
            <circle cx="17" cy="18" r="2" />
          </svg>
          {!collapsed && <span>Search Car Rentals</span>}
        </Link>

        <Link
          to={`/${accountName}${Routes.Cruises}`}
          className={`modern-sidebar__nav-item ${selectedItem === 'cruise-icon' ? 'modern-sidebar__nav-item--active' : ''}`}
          title="Search Cruises"
          onClick={() => handleLinkClick('cruise-icon')}
        >
          <svg
            className="modern-sidebar__nav-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 18h18M4.5 14.5L3 18m18 0l-1.5-3.5M6 14V6h4v8m4 0V2h4v12m-8-8h4"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 18c0 1.657 4.03 3 9 3s9-1.343 9-3"
            />
          </svg>
          {!collapsed && <span>Search Cruises</span>}
        </Link>

        <Link
          to={`/${accountName}${Routes.AllInclusive}`}
          className={`modern-sidebar__nav-item ${selectedItem === 'all-inclusive-icon' ? 'modern-sidebar__nav-item--active' : ''}`}
          title="Search All Inclusives"
          onClick={() => handleLinkClick('all-inclusive-icon')}
        >
          <svg
            className="modern-sidebar__nav-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v6m0 0c-2 0-3.5 1-3.5 3s1.5 3 3.5 3 3.5-1 3.5-3-1.5-3-3.5-3zm0 6v9m-7-5l2-2m10 2l-2-2M5 19h14"
            />
          </svg>
          {!collapsed && <span>Search All Inclusives</span>}
        </Link>
      </nav>

      <div className="modern-sidebar__footer">
        {!collapsed ? (
          <>
            <div className="modern-sidebar__user">
              <div className="modern-sidebar__user-avatar">{getInitials(userName)}</div>
              <div className="modern-sidebar__user-info">
                <p className="modern-sidebar__user-name">{userName}</p>
                <p className="modern-sidebar__user-id">ID: {memberId}</p>
                {points !== undefined && points !== null && (
                  <p className="modern-sidebar__user-points">Points: {points.toLocaleString()}</p>
                )}
              </div>
            </div>
            <div className="modern-sidebar__account-section">
              <p className="modern-sidebar__account-title">My Account</p>
              <Link
                to={`/${accountName}${Routes.Reservations}`}
                className={`modern-sidebar__footer-link ${selectedItem === 'bookings-icon' ? 'modern-sidebar__nav-item--active' : ''}`}
                onClick={() => handleLinkClick('bookings-icon')}
              >
                <svg
                  className="modern-sidebar__footer-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                My Bookings
              </Link>
              <Link
                to={`/${accountName}${Routes.SavedPropertiesAccount}`}
                className={`modern-sidebar__footer-link ${selectedItem === 'favorite-properties-icon' ? 'modern-sidebar__nav-item--active' : ''}`}
                onClick={() => handleLinkClick('favorite-properties-icon')}
              >
                <svg
                  className="modern-sidebar__footer-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                Favorite Properties
              </Link>
              <Link
                to={`/${accountName}${Routes.ChangePassword}`}
                className={`modern-sidebar__footer-link ${selectedItem === 'change-password-icon' ? 'modern-sidebar__nav-item--active' : ''}`}
                onClick={() => handleLinkClick('change-password-icon')}
              >
                <svg
                  className="modern-sidebar__footer-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Change Password
              </Link>
            </div>
            {(() => {
              const isPrivateTokenAccountType = account?.type === LoginType.PrivateWithToken;

              const handleLogout = () => {
                dispatch(logout());
                const isPublicAccountType = account?.type === LoginType.Public;
                const hasClientCash = account?.hasClientCash;
                if (!isPublicAccountType || !hasClientCash) {
                  const { pathname, search } = history.location;
                  dispatch(setLoginRedirect(`${pathname}${isEmpty(search) ? '' : search}`));
                  history.push(`/${accountName}${Routes.Login}`);
                }
              };

              const handleLogoutToken = () => {
                Cookies.remove(COOKIE_TOKEN_NAME);
                Cookies.remove(COOKIE1_TOKEN_NAME);
              };

              // For PrivateWithToken accounts, use logoutUrl from menu or preferences
              if (isPrivateTokenAccountType) {
                const logoutURLPref = getPreferenceFromAccount(account as IAccount, LOGOUT_FIELD_KEY);
                const logoutUrl = !isEmpty(items?.logoutUrl)
                  ? items?.logoutUrl
                  : !isEmpty(logoutURLPref)
                  ? logoutURLPref
                  : null;

                if (logoutUrl) {
                  return (
                    <a
                      href={logoutUrl}
                      onClick={handleLogoutToken}
                      className="modern-sidebar__footer-link modern-sidebar__footer-link--logout"
                    >
                      <svg
                        className="modern-sidebar__footer-icon"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      Logout
                    </a>
                  );
                }
              }

              // For PrivateWithLogin accounts or other types, use logout action
              return (
                <div
                  onClick={handleLogout}
                  className="modern-sidebar__footer-link modern-sidebar__footer-link--logout"
                  style={{ cursor: 'pointer' }}
                >
                  <svg
                    className="modern-sidebar__footer-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Logout
                </div>
              );
            })()}
          </>
        ) : (
          <div className="modern-sidebar__footer-collapsed">
            <div className="modern-sidebar__user-avatar" title={userName}>
              {getInitials(userName)}
            </div>
            <Link 
              to={`/${accountName}${Routes.Reservations}`}
              title="My Bookings"
              onClick={() => handleLinkClick('bookings-icon')}
              className={`${selectedItem === 'bookings-icon' ? 'modern-sidebar__nav-item  modern-sidebar__nav-item--active' : ''}`}
            >
              <svg
                className="modern-sidebar__footer-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </Link>
            <Link
              to={`/${accountName}${Routes.SavedPropertiesAccount}`}
              title="Favorite Properties"
              className={`${selectedItem === 'favorite-properties-icon' ? 'modern-sidebar__nav-item modern-sidebar__nav-item--active' : ''}`}
              onClick={() => handleLinkClick('favorite-properties-icon')}
            >
              <svg
                className="modern-sidebar__footer-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </Link>
            <Link 
              to={`/${accountName}${Routes.ChangePassword}`} 
              title="Change Password" 
              onClick={() => handleLinkClick('change-password-icon')}
              className={`${selectedItem === 'change-password-icon' ? 'modern-sidebar__nav-item  modern-sidebar__nav-item--active' : ''}`}
            >
              <svg
                className="modern-sidebar__footer-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </Link>
            {(() => {
              const isPrivateTokenAccountType = account?.type === LoginType.PrivateWithToken;

              const handleLogoutToken = () => {
                Cookies.remove(COOKIE_TOKEN_NAME);
                Cookies.remove(COOKIE1_TOKEN_NAME);
              };

              const handleLogout = () => {
                dispatch(logout());
                const isPublicAccountType = account?.type === LoginType.Public;
                const hasClientCash = account?.hasClientCash;
                if (!isPublicAccountType || !hasClientCash) {
                  const { pathname, search } = history.location;
                  dispatch(setLoginRedirect(`${pathname}${isEmpty(search) ? '' : search}`));
                  history.push(`/${accountName}${Routes.Login}`);
                }
              };

              // For PrivateWithToken accounts, use logoutUrl from menu or preferences
              if (isPrivateTokenAccountType) {
                const logoutURLPref = getPreferenceFromAccount(account as IAccount, LOGOUT_FIELD_KEY);
                const logoutUrl = !isEmpty(items?.logoutUrl)
                  ? items?.logoutUrl
                  : !isEmpty(logoutURLPref)
                  ? logoutURLPref
                  : null;

                if (logoutUrl) {
                  return (
                    <a
                      href={logoutUrl}
                      onClick={handleLogoutToken}
                      title="Logout"
                    >
                      <svg
                        className="modern-sidebar__footer-icon modern-sidebar__footer-icon--logout"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                    </a>
                  );
                }
              }

              // For PrivateWithLogin accounts or other types, use logout action
              return (
                <div
                  onClick={handleLogout}
                  title="Logout"
                  style={{ cursor: 'pointer' }}
                >
                  <svg
                    className="modern-sidebar__footer-icon modern-sidebar__footer-icon--logout"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </aside>
  );
}
